import React from 'react';
import { Sparkles, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import useLiveCopilot from '../../hooks/useLiveCopilot';
import ArticleText from './ArticleText';

// Live Copilot Phase 1 (2026-09-24, backlog register #11) — the reactive
// knowledge-answer chip. Placement: the AI Assist rail (Storm, 2026-09-24 —
// above-composer only makes sense once chat ships; the rail is the only
// real surface today). Replaces the "Knowledge Base — Coming soon" stub
// that section always was (fed by display.kbArticles, which nothing has
// ever populated — see vault #237's own note that this surfacing was
// always meant to be one build off proactive retrieval).
//
// Same idle-state discipline as CoachingPanel.jsx: hides entirely if the
// endpoint isn't configured, a quiet one-liner when watching but nothing
// has fired yet, never "Coming soon" once the feature is actually live.
//
// "Use this" mirrors the exact copy-to-clipboard pattern Suggested
// Responses already uses — no new interaction pattern invented for v1;
// "Insert to chat" from the spec doesn't apply here (voice has no
// composer).
// 2026-10-20 (Storm 'go labels'): Public = the AI can say this to callers;
// Internal = the team's own knowledge, never read out to callers. Same words as
// the client portal's Search tab.
export function KnowledgeTier({ tier }) {
  const internal = tier === 'agent';
  return (
    <div className={`kb-tier ${internal ? 'kb-tier-internal' : 'kb-tier-public'}`}
      title={internal ? 'Your team only. Do not read this out to the caller.' : 'Public. The AI can say this to callers.'}>
      {internal ? 'Internal: team only, do not read out' : 'Public: OK to say to the caller'}
    </div>
  );
}

export default function LiveCopilotChip() {
  const { chips, markClicked, pending, error, enabled } = useLiveCopilot();
  if (!enabled) return null;

  function use(c) {
    const text = c?.hits?.[0]?.text;
    if (text) navigator.clipboard?.writeText(text).catch(() => {});
    markClicked(c.id);
  }

  return (
    <div className="rr-section live-copilot" aria-live="polite">
      <div className="rr-section-title live-copilot-title">
        <Sparkles size={12} />
        Knowledge Base
        {pending && <Loader2 size={11} className="spin live-copilot-spin" />}
      </div>

      {error && (
        <div className="live-copilot-error">
          <AlertCircle size={12} />
          <span>{error}</span>
        </div>
      )}

      {chips.length ? (
        // Up to three, newest first; older ones dim (rank-1, rank-2) and the
        // oldest drops off when a fourth arrives (Storm 23 Oct).
        chips.map((c, i) => (
          <div key={c.id} className={`live-copilot-cue rank-${i}`}>
            <div className="live-copilot-cue-question">
              <ExternalLink size={12} />
              {c.question}
            </div>
            <KnowledgeTier tier={c.tier} />
            <div className="live-copilot-cue-text"><ArticleText text={c.hits[0].text} /></div>
            <button className="use-btn live-copilot-use" onClick={() => use(c)}>Use this</button>
          </div>
        ))
      ) : (
        !error && <p className="panel-empty live-copilot-idle">Watching the conversation — nothing to show yet.</p>
      )}
    </div>
  );
}
